import React, { useState, useEffect, useReducer, useContext, useMemo } from "react";
import { toast } from "react-toastify";
import {
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addMonths,
  addWeeks,
  addDays,
  format,
} from "date-fns";
import { es } from "date-fns/locale";
import {
  Plus,
  ChevronLeft,
  ChevronRight,
  CalendarDays,
  CalendarRange,
  List as ListIcon,
  Search,
  Tag,
  Clock,
  Settings as SettingsIcon,
} from "lucide-react";

import openSocket from "../../services/socket-io";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import ConfirmationModal from "../../components/ConfirmationModal";
import AppointmentModal from "../../components/AppointmentModal";
import AppointmentReasonsModal from "../../components/AppointmentReasonsModal";
import AppointmentAvailabilityModal from "../../components/AppointmentAvailabilityModal";
import AppointmentSettingsTab from "../../components/AppointmentSettingsTab";
import MonthView from "../../components/AppointmentCalendar/MonthView";
import WeekView from "../../components/AppointmentCalendar/WeekView";
import ListView from "../../components/AppointmentCalendar/ListView";
import { dayAtHour, APPOINTMENT_STATUS } from "../../components/AppointmentCalendar/appointmentUtils";
import { Can } from "../../components/Can";
import { checkPermission } from "../../rules";
import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const TABS = [
  { id: "calendar", label: "Calendario", icon: CalendarDays },
  { id: "settings", label: "Configuración", icon: SettingsIcon },
];

const reducer = (state, action) => {
  if (action.type === "LOAD_APPOINTMENTS") {
    return action.payload;
  }
  if (action.type === "UPDATE_APPOINTMENT") {
    const appointment = action.payload;
    const idx = state.findIndex((a) => a.id === appointment.id);
    if (idx !== -1) {
      const next = [...state];
      next[idx] = appointment;
      return next;
    }
    return [appointment, ...state];
  }
  if (action.type === "DELETE_APPOINTMENT") {
    return state.filter((a) => a.id !== action.payload);
  }
  if (action.type === "RESET") return [];
  return state;
};

const VIEWS = [
  { key: "month", label: "Mes", icon: CalendarDays },
  { key: "week", label: "Semana", icon: CalendarRange },
  { key: "list", label: "Lista", icon: ListIcon },
];

const selectClass =
  "px-2.5 py-2 text-sm bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-700 dark:text-slate-300 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all";

const Appointments = () => {
  const { user } = useContext(AuthContext);

  const [activeTab, setActiveTab] = useState("calendar");
  const [view, setView] = useState("month");
  const [currentDate, setCurrentDate] = useState(new Date());
  const [appointments, dispatch] = useReducer(reducer, []);
  const [loading, setLoading] = useState(false);

  const [searchParam, setSearchParam] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [reasonFilter, setReasonFilter] = useState("");
  const [professionalFilter, setProfessionalFilter] = useState("");

  const [reasons, setReasons] = useState([]);
  const [users, setUsers] = useState([]);

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [prefilledDate, setPrefilledDate] = useState("");
  const [reasonsModalOpen, setReasonsModalOpen] = useState(false);
  const [availabilityModalOpen, setAvailabilityModalOpen] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const canDelete =
    user.profile === "admin" ||
    checkPermission(user.permissions, "appointments:delete");
  const canEditSettings =
    user.profile === "admin" ||
    checkPermission(user.permissions, "appointments:availability");

  // Visible window, driven by the active view.
  const range = useMemo(() => {
    if (view === "week") {
      return {
        from: startOfWeek(currentDate, { weekStartsOn: 1 }),
        to: endOfWeek(currentDate, { weekStartsOn: 1 }),
      };
    }
    if (view === "list") {
      // The list is forward-looking: from today through the next 60 days.
      return { from: currentDate, to: addDays(currentDate, 60) };
    }
    return {
      from: startOfWeek(startOfMonth(currentDate), { weekStartsOn: 1 }),
      to: endOfWeek(endOfMonth(currentDate), { weekStartsOn: 1 }),
    };
  }, [view, currentDate]);

  useEffect(() => {
    const load = async () => {
      try {
        const [reasonsRes, usersRes] = await Promise.all([
          api.get("/appointment-reasons"),
          api.get("/users", { params: { pageNumber: 1 } }),
        ]);
        setReasons(reasonsRes.data || []);
        setUsers(usersRes.data?.users || []);
      } catch (err) {
        toastError(err);
      }
    };
    load();
  }, []);

  const fetchAppointments = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/appointments", {
        params: {
          from: range.from.toISOString(),
          to: range.to.toISOString(),
          status: statusFilter || undefined,
          reasonId: reasonFilter || undefined,
          professionalId: professionalFilter || undefined,
          searchParam: searchParam || undefined,
        },
      });
      dispatch({ type: "LOAD_APPOINTMENTS", payload: data.appointments });
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(fetchAppointments, 500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range.from, range.to, statusFilter, reasonFilter, professionalFilter, searchParam]);

  useEffect(() => {
    const socket = openSocket();

    const handleAppointment = (data) => {
      if (data.action === "create" || data.action === "update") {
        dispatch({ type: "UPDATE_APPOINTMENT", payload: data.appointment });
      }
      if (data.action === "delete") {
        dispatch({ type: "DELETE_APPOINTMENT", payload: +data.appointmentId });
      }
    };

    const handleReason = () => {
      api
        .get("/appointment-reasons")
        .then(({ data }) => setReasons(data || []))
        .catch(() => {});
    };

    socket.on("appointment", handleAppointment);
    socket.on("appointmentReason", handleReason);

    return () => {
      socket.off("appointment", handleAppointment);
      socket.off("appointmentReason", handleReason);
    };
  }, []);

  const openNew = (date, hour) => {
    setSelectedAppointment(null);
    setPrefilledDate(date ? dayAtHour(date, hour ?? 9) : "");
    setModalOpen(true);
  };

  const openExisting = (appointment) => {
    setSelectedAppointment(appointment);
    setPrefilledDate("");
    setModalOpen(true);
  };

  const handleDelete = async (appointment) => {
    try {
      await api.delete(`/appointments/${appointment.id}`);
      toast.success("Cita eliminada");
    } catch (err) {
      toastError(err);
    }
    setDeleting(null);
  };

  const shift = (direction) => {
    if (view === "week") setCurrentDate((d) => addWeeks(d, direction));
    else if (view === "list") setCurrentDate((d) => addDays(d, direction * 30));
    else setCurrentDate((d) => addMonths(d, direction));
  };

  const periodLabel = () => {
    if (view === "week") {
      return `${format(range.from, "d MMM", { locale: es })} – ${format(range.to, "d MMM yyyy", { locale: es })}`;
    }
    if (view === "list") {
      return `Desde ${format(range.from, "d MMM yyyy", { locale: es })}`;
    }
    return format(currentDate, "MMMM yyyy", { locale: es });
  };

  return (
    <MainContainer>
      <ConfirmationModal
        title={deleting ? `¿Eliminar la cita de ${deleting.contact?.name}?` : ""}
        open={confirmOpen}
        onClose={setConfirmOpen}
        onConfirm={() => handleDelete(deleting)}
      >
        Esta acción no se puede deshacer. El contacto no recibirá ningún aviso.
      </ConfirmationModal>

      <AppointmentModal
        open={modalOpen}
        onClose={() => { setModalOpen(false); setSelectedAppointment(null); setPrefilledDate(""); }}
        appointmentId={selectedAppointment?.id}
        initialScheduledAt={prefilledDate}
        onSaved={(appointment) => dispatch({ type: "UPDATE_APPOINTMENT", payload: appointment })}
      />

      <AppointmentReasonsModal
        open={reasonsModalOpen}
        onClose={() => setReasonsModalOpen(false)}
        onChanged={fetchAppointments}
      />

      <AppointmentAvailabilityModal
        open={availabilityModalOpen}
        onClose={() => setAvailabilityModalOpen(false)}
      />

      <MainHeader>
        <Title>Citas</Title>
        {activeTab === "calendar" && (
          <MainHeaderButtonsWrapper>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="search"
                placeholder="Buscar contacto..."
                value={searchParam}
                onChange={(e) => setSearchParam(e.target.value)}
                className="pl-9 pr-4 py-2 text-sm bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg placeholder-slate-400 focus:ring-2 focus:ring-indigo-500 focus:border-transparent w-44 transition-all"
              />
            </div>
            <Can
              role={user.profile}
              perform="appointments:availability"
              permissions={user.permissions}
              yes={() => (
                <button
                  onClick={() => setAvailabilityModalOpen(true)}
                  className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
                >
                  <Clock className="w-4 h-4" />
                  Horarios
                </button>
              )}
            />
            <Can
              role={user.profile}
              perform="appointments:reasons"
              permissions={user.permissions}
              yes={() => (
                <button
                  onClick={() => setReasonsModalOpen(true)}
                  className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
                >
                  <Tag className="w-4 h-4" />
                  Motivos
                </button>
              )}
            />
            <Can
              role={user.profile}
              perform="appointments:create"
              permissions={user.permissions}
              yes={() => (
                <button
                  onClick={() => openNew()}
                  className="flex items-center gap-2 px-3 py-2 text-sm font-medium bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors shadow-sm"
                >
                  <Plus className="w-4 h-4" />
                  Nueva cita
                </button>
              )}
            />
          </MainHeaderButtonsWrapper>
        )}
      </MainHeader>

      <div className="px-6">
        <div className="flex gap-1 border-b border-slate-200 dark:border-slate-800 mb-4">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? "border-indigo-600 text-indigo-600"
                  : "border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {activeTab === "settings" && (
        <div className="flex-1 overflow-y-auto px-6 pb-6">
          <AppointmentSettingsTab canEdit={canEditSettings} />
        </div>
      )}

      {activeTab === "calendar" && (
      <div className="flex-1 overflow-y-auto px-6 pb-6 space-y-4">
        {/* Toolbar */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <button
              onClick={() => shift(-1)}
              className="p-2 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-500 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              onClick={() => setCurrentDate(new Date())}
              className="px-3 py-2 text-sm font-medium rounded-lg border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
            >
              Hoy
            </button>
            <button
              onClick={() => shift(1)}
              className="p-2 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-500 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
            <span className="ml-2 text-sm font-semibold text-slate-700 dark:text-slate-200 capitalize">
              {periodLabel()}
            </span>
            {loading && (
              <span className="w-4 h-4 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className={selectClass}
            >
              <option value="">Todos los estados</option>
              {Object.entries(APPOINTMENT_STATUS).map(([key, meta]) => (
                <option key={key} value={key}>{meta.label}</option>
              ))}
            </select>
            <select
              value={reasonFilter}
              onChange={(e) => setReasonFilter(e.target.value)}
              className={selectClass}
            >
              <option value="">Todos los motivos</option>
              {reasons.map((reason) => (
                <option key={reason.id} value={reason.id}>{reason.name}</option>
              ))}
            </select>
            <select
              value={professionalFilter}
              onChange={(e) => setProfessionalFilter(e.target.value)}
              className={selectClass}
            >
              <option value="">Todos los profesionales</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>{u.name}</option>
              ))}
            </select>

            <div className="flex items-center rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-0.5">
              {VIEWS.map((v) => {
                const Icon = v.icon;
                return (
                  <button
                    key={v.key}
                    onClick={() => setView(v.key)}
                    className={`flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold rounded-md transition-all ${
                      view === v.key
                        ? "bg-indigo-600 text-white shadow-sm"
                        : "text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-300"
                    }`}
                  >
                    <Icon className="w-3.5 h-3.5" />
                    {v.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {view === "month" && (
          <MonthView
            currentDate={currentDate}
            appointments={appointments}
            onSelectAppointment={openExisting}
            onSelectSlot={(day) => openNew(day)}
          />
        )}
        {view === "week" && (
          <WeekView
            currentDate={currentDate}
            appointments={appointments}
            onSelectAppointment={openExisting}
            onSelectSlot={(day, hour) => openNew(day, hour)}
          />
        )}
        {view === "list" && (
          <ListView
            appointments={appointments}
            onSelectAppointment={openExisting}
            canDelete={canDelete}
            onDelete={(appointment) => { setDeleting(appointment); setConfirmOpen(true); }}
          />
        )}

        {/* Status legend */}
        <div className="flex flex-wrap items-center gap-4 pt-1">
          {Object.entries(APPOINTMENT_STATUS).map(([key, meta]) => (
            <span key={key} className="flex items-center gap-1.5 text-[11px] text-slate-500 dark:text-slate-400">
              <span className={`w-2 h-2 rounded-full ${meta.dot}`} />
              {meta.label}
            </span>
          ))}
        </div>
      </div>
      )}
    </MainContainer>
  );
};

export default Appointments;
